import type { EpocusExamType } from "elephant-web/gql";

type FindingValue = "absent" | "present" | "indeterminate" | "not_assessed";
type EffusionSize = "none" | "small" | "moderate" | "large";

export type EFastWorksheetData = {
  examType: "efast";
  indication: "blunt_trauma" | "penetrating_trauma" | "hypotension" | null;
  views: {
    ruq: boolean;
    luq: boolean;
    suprapubic: boolean;
    subxiphoid: boolean;
    rLung: boolean;
    lLung: boolean;
  };
  findings: {
    ruqFreeFluid: FindingValue;
    luqFreeFluid: FindingValue;
    pelvicFreeFluid: FindingValue;
    pericardialEffusion: FindingValue;
    rPneumothorax: FindingValue;
    lPneumothorax: FindingValue;
  };
  notes: string | null;
};

export type CardiacWorksheetData = {
  examType: "cardiac";
  indication: "dyspnea" | "hypotension" | "chest_pain" | "arrest" | null;
  views: {
    plax: boolean;
    psax: boolean;
    apical4Chamber: boolean;
    subxiphoid: boolean;
  };
  findings: {
    lvFunction: "normal" | "reduced" | "severely_reduced" | null;
    rvDilatation: boolean | null;
    pericardialEffusion: EffusionSize | null;
    ivcCollapsible: boolean | null;
    ivcDiameterCm: number | null;
    spectralDoppler: boolean | null;
    colorFlowDoppler: boolean | null;
  };
  notes: string | null;
};

export type LungZoneFindings = {
  lungSliding: boolean | null;
  bLines: "0" | "1-2" | "3+" | "confluent" | null;
  pleuralEffusion: EffusionSize | null;
  consolidation: boolean | null;
};

export type LungWorksheetData = {
  examType: "lung";
  indication: "dyspnea" | "hypotension" | "trauma" | null;
  views: {
    rUpperAnterior: boolean;
    rLowerAnterior: boolean;
    rPosterior: boolean;
    lUpperAnterior: boolean;
    lLowerAnterior: boolean;
    lPosterior: boolean;
  };
  zones: {
    rUpper: LungZoneFindings;
    rLower: LungZoneFindings;
    lUpper: LungZoneFindings;
    lLower: LungZoneFindings;
  };
  notes: string | null;
};

export type BladderWorksheetData = {
  examType: "bladder";
  indication:
    | "urinary_retention"
    | "abdominal_pain"
    | "post_void_residual"
    | null;
  views: { sagittal: boolean; transverse: boolean };
  findings: {
    bladderVolumeMethod: "auto" | "manual" | null;
    bladderVolumeMl: number | null;
    postVoidVolumeMl: number | null;
    distension: "normal" | "mild" | "moderate" | "severe" | null;
    wallAbnormalities: boolean | null;
  };
  notes: string | null;
};

export type KidneyFindings = {
  sizeLengthCm: number | null;
  hydronephrosis: "none" | "mild" | "moderate" | "severe" | null;
  stones: boolean | null;
  perinephricFluid: boolean | null;
  cystOrMass: boolean | null;
};

export type RenalWorksheetData = {
  examType: "renal";
  indication: "flank_pain" | "hematuria" | "aki" | null;
  views: {
    rKidneyLong: boolean;
    rKidneyTrans: boolean;
    lKidneyLong: boolean;
    lKidneyTrans: boolean;
    bladder: boolean;
  };
  findings: {
    rKidney: KidneyFindings;
    lKidney: KidneyFindings;
    bladderAssessed: boolean | null;
  };
  notes: string | null;
};

export type AortaSegmentFindings = {
  diameterCm: number | null;
  aneurysm: boolean | null;
  dissectionFlap: boolean | null;
  muralThrombus: boolean | null;
};

export type AortaWorksheetData = {
  examType: "aorta";
  indication:
    | "abdominal_pain"
    | "pulsatile_mass"
    | "syncope"
    | "aaa_screening"
    | null;
  views: {
    proximalTrans: boolean;
    proximalSag: boolean;
    midTrans: boolean;
    midSag: boolean;
    distalTrans: boolean;
    distalSag: boolean;
  };
  findings: {
    proximal: AortaSegmentFindings;
    mid: AortaSegmentFindings;
    distal: AortaSegmentFindings;
  };
  notes: string | null;
};

export type PregnancyCardiacActivity = "present" | "absent" | "not_assessed";

export type PregnancyFreeFluid = "none" | "small" | "moderate" | "large";

export type FetalPresentation =
  | "cephalic"
  | "breech"
  | "transverse"
  | "oblique"
  | "unknown";

export type PregnancyWorksheetData = {
  examType: "pregnancy";
  indication:
    | "dating"
    | "vaginal_bleeding"
    | "pelvic_pain"
    | "suspected_ectopic"
    | "hyperemesis"
    | null;
  views: {
    transabdominalSagittal: boolean;
    transabdominalTransverse: boolean;
    transvaginalSagittal: boolean;
    transvaginalTransverse: boolean;
  };
  findings: {
    intrauterineGestation: boolean | null;
    gestationalSacSeen: boolean | null;
    yolkSacSeen: boolean | null;
    fetalPoleSeen: boolean | null;
    cardiacActivity: PregnancyCardiacActivity | null;
    fetalHeartRateBpm: number | null;
    numberOfGestations: number | null;
    // Biometry (mm)
    crownRumpLengthMm: number | null;
    meanSacDiameterMm: number | null;
    biparietalDiameterMm: number | null;
    headCircumferenceMm: number | null;
    abdominalCircumferenceMm: number | null;
    femurLengthMm: number | null;
    // Derived: estimated fetal weight (grams) — Hadlock IV from BPD/HC/AC/FL.
    estimatedFetalWeightG: number | null;
    // Dating
    lmpDate: string | null;
    estimatedGestationalAgeWeeks: number | null;
    estimatedDueDate: string | null;
    // Obstetrical
    fetalPresentation: FetalPresentation | null;
    amnioticFluidIndexCm: number | null;
    // Gynecological / cul-de-sac
    uterineFibroidsSeen: boolean | null;
    pelvicFreeFluid: PregnancyFreeFluid | null;
  };
  notes: string | null;
};

export const MSK_REGIONS = [
  "shoulder",
  "knee",
  "ankle",
  "achilles",
  "wristHand",
  "other",
] as const;

export type MskRegion = (typeof MSK_REGIONS)[number];

type MskIndicationMeta = {
  region: MskRegion;
  label: string;
  guided?: true;
};

export const MSK_INDICATIONS = {
  shoulder_effusion_synovitis: {
    region: "shoulder",
    label: "Suspected Glenohumeral Effusion / Synovitis",
  },
  shoulder_rotator_cuff_tear: {
    region: "shoulder",
    label: "Suspected Rotator Cuff Tear",
  },
  shoulder_biceps_tendon: {
    region: "shoulder",
    label: "Suspected Biceps Tendon Pathology",
  },
  shoulder_subacromial_bursitis: {
    region: "shoulder",
    label: "Suspected Subacromial-Subdeltoid Bursitis",
  },
  shoulder_guided_procedure: {
    region: "shoulder",
    label: "Guided Aspiration / Injection",
    guided: true,
  },
  knee_effusion: { region: "knee", label: "Suspected Effusion" },
  knee_septic_arthritis: {
    region: "knee",
    label: "Suspected Septic Arthritis",
  },
  knee_bakers_cyst: { region: "knee", label: "Suspected Baker's Cyst" },
  knee_extensor_tendon_injury: {
    region: "knee",
    label: "Suspected Quadriceps / Patellar Tendon Injury",
  },
  knee_guided_procedure: {
    region: "knee",
    label: "Guided Aspiration / Injection",
    guided: true,
  },
  ankle_effusion: { region: "ankle", label: "Suspected Effusion" },
  ankle_atfl_injury: {
    region: "ankle",
    label: "Suspected Anterior Talofibular Ligament (ATFL) Injury",
  },
  ankle_tendon_pathology: {
    region: "ankle",
    label: "Suspected Tendon Pathology (Peroneal / Tibialis Posterior)",
  },
  ankle_guided_procedure: {
    region: "ankle",
    label: "Guided Aspiration / Injection",
    guided: true,
  },
  achilles_rupture: {
    region: "achilles",
    label: "Suspected Achilles Rupture",
  },
  achilles_tendinopathy: {
    region: "achilles",
    label: "Suspected Tendinopathy",
  },
  achilles_follow_up: {
    region: "achilles",
    label: "Post-Injury / Post-Repair Follow-up",
  },
  wrist_tenosynovitis: {
    region: "wristHand",
    label: "Suspected Tenosynovitis",
  },
  wrist_tendon_rupture: {
    region: "wristHand",
    label: "Suspected Tendon Rupture",
  },
  wrist_ganglion: { region: "wristHand", label: "Suspected Ganglion Cyst" },
  wrist_foreign_body: {
    region: "wristHand",
    label: "Suspected Soft Tissue Foreign Body",
  },
  wrist_guided_injection: {
    region: "wristHand",
    label: "Guided Injection",
    guided: true,
  },
  other_trauma: { region: "other", label: "Trauma" },
  other_pain_swelling: {
    region: "other",
    label: "Pain/swelling/erythema at above location",
  },
  other_fever: { region: "other", label: "Fever" },
  other_other: { region: "other", label: "Other" },
} as const satisfies Record<string, MskIndicationMeta>;

export type MskIndication = keyof typeof MSK_INDICATIONS;

export type MskSide = "left" | "right" | "bilateral";

export type MskFindingValue = string | number | string[] | null;

export type MskBillingLevel = "complete" | "limited";

export type MskRegionData = {
  side: MskSide | null;
  indication: MskIndication | null;
  findings: Record<string, MskFindingValue>;
  billingLevel?: MskBillingLevel | null;
  technicallyLimited?: boolean;
};

export type MskWorksheetData = {
  examType: "msk";
  definitionVersion?: number;
  indication: MskIndication | null;
  views: Record<string, boolean>;
  regions: Record<MskRegion, MskRegionData>;
  notes: string | null;
};

export const PROCEDURE_EXAM_TYPES = [
  "guided_joint_injection",
  "guided_tendon_injection",
  "guided_nerve_block",
] as const;

export type ProcedureExamType = (typeof PROCEDURE_EXAM_TYPES)[number];

export type ProcedureFindingValue = string | number | string[] | null;

export type ProcedureWorksheetData = {
  examType: ProcedureExamType;
  definitionVersion: number;
  indication: string[];
  views: Record<string, boolean>;
  findings: Record<string, ProcedureFindingValue>;
  notes: string | null;
};

export const US_EXAM_TYPES = [
  "us_efast",
  "us_cardiac",
  "us_thoracic",
  "us_renal",
  "us_bladder",
  "us_aorta",
  "us_ob_transabdominal",
  "us_cardiac_thoracic",
  "us_rush",
  "us_biliary",
  "us_biliary_renal",
  "us_appendix",
  "us_sbo",
  "us_ob_transvaginal",
  "us_dvt",
  "us_soft_tissue",
  "us_ocular",
  "us_testicular",
] as const;

export type UsExamType = (typeof US_EXAM_TYPES)[number];

export type UsSide = "right" | "left" | "both";

export type UsSidedAnswer = { value: string; side: UsSide | null };

export type UsFindingValue =
  | string
  | number
  | readonly (string | UsSidedAnswer)[]
  | null;

export type UsWorksheetData = {
  examType: UsExamType;
  definitionVersion: number;
  indication: string[];
  views: Record<string, boolean>;
  findings: Record<string, UsFindingValue>;
  technicallyLimited: Record<string, boolean>;
  notes: string | null;
};

export type ExamWorksheetData =
  | EFastWorksheetData
  | CardiacWorksheetData
  | LungWorksheetData
  | BladderWorksheetData
  | RenalWorksheetData
  | AortaWorksheetData
  | PregnancyWorksheetData
  | MskWorksheetData
  | ProcedureWorksheetData
  | UsWorksheetData;

export type ExamType = ExamWorksheetData["examType"];

export type ExamImage = {
  id: string;
  previewS3Key: string;
  viewLabel: string | null;
  // PNE-202: raw GE ImageType[2] value (e.g. "TTE", "CHEST") — null for non-GE or unknown.
  // Optional for backward compat: documents created before this field was added won't have it.
  dicomPreset?: string | null;
  // PNE-210: mm per pixel from DICOM tag (0018,602C) — null if tag absent or non-GE device.
  // Optional for backward compat: documents created before this field was added won't have it.
  physicalDeltaMm?: number | null;
  mskRegion?: MskRegion | null;
  needleInTarget?: boolean;
};

// ─── Normal defaults per exam type (used by All Normal toggle) ─────────────

const NORMAL_FINDING: FindingValue = "absent";

const NORMAL_EFAST_WORKSHEET: EFastWorksheetData = {
  examType: "efast",
  indication: null,
  views: {
    ruq: true,
    luq: true,
    suprapubic: true,
    subxiphoid: true,
    rLung: true,
    lLung: true,
  },
  findings: {
    ruqFreeFluid: NORMAL_FINDING,
    luqFreeFluid: NORMAL_FINDING,
    pelvicFreeFluid: NORMAL_FINDING,
    pericardialEffusion: NORMAL_FINDING,
    rPneumothorax: NORMAL_FINDING,
    lPneumothorax: NORMAL_FINDING,
  },
  notes: null,
};

const NORMAL_CARDIAC_WORKSHEET: CardiacWorksheetData = {
  examType: "cardiac",
  indication: null,
  views: { plax: true, psax: true, apical4Chamber: true, subxiphoid: true },
  findings: {
    lvFunction: "normal",
    rvDilatation: false,
    pericardialEffusion: "none",
    ivcCollapsible: true,
    ivcDiameterCm: 1.5,
    spectralDoppler: false,
    colorFlowDoppler: false,
  },
  notes: null,
};

const NORMAL_LUNG_ZONE: LungZoneFindings = {
  lungSliding: true,
  bLines: "0",
  pleuralEffusion: "none",
  consolidation: false,
};

const NORMAL_LUNG_WORKSHEET: LungWorksheetData = {
  examType: "lung",
  indication: null,
  views: {
    rUpperAnterior: true,
    rLowerAnterior: true,
    rPosterior: true,
    lUpperAnterior: true,
    lLowerAnterior: true,
    lPosterior: true,
  },
  zones: {
    rUpper: { ...NORMAL_LUNG_ZONE },
    rLower: { ...NORMAL_LUNG_ZONE },
    lUpper: { ...NORMAL_LUNG_ZONE },
    lLower: { ...NORMAL_LUNG_ZONE },
  },
  notes: null,
};

const NORMAL_BLADDER_WORKSHEET: BladderWorksheetData = {
  examType: "bladder",
  indication: null,
  views: { sagittal: true, transverse: true },
  findings: {
    bladderVolumeMethod: "auto",
    bladderVolumeMl: 0,
    postVoidVolumeMl: null,
    distension: "normal",
    wallAbnormalities: false,
  },
  notes: null,
};

const NORMAL_KIDNEY: KidneyFindings = {
  sizeLengthCm: 11,
  hydronephrosis: "none",
  stones: false,
  perinephricFluid: false,
  cystOrMass: false,
};

const NORMAL_RENAL_WORKSHEET: RenalWorksheetData = {
  examType: "renal",
  indication: null,
  views: {
    rKidneyLong: true,
    rKidneyTrans: true,
    lKidneyLong: true,
    lKidneyTrans: true,
    bladder: true,
  },
  findings: {
    rKidney: { ...NORMAL_KIDNEY },
    lKidney: { ...NORMAL_KIDNEY },
    bladderAssessed: true,
  },
  notes: null,
};

const NORMAL_AORTA_SEGMENT: AortaSegmentFindings = {
  diameterCm: 2.0,
  aneurysm: false,
  dissectionFlap: false,
  muralThrombus: false,
};

const NORMAL_PREGNANCY_WORKSHEET: PregnancyWorksheetData = {
  examType: "pregnancy",
  indication: null,
  views: {
    transabdominalSagittal: true,
    transabdominalTransverse: true,
    transvaginalSagittal: false,
    transvaginalTransverse: false,
  },
  findings: {
    intrauterineGestation: true,
    gestationalSacSeen: true,
    yolkSacSeen: true,
    fetalPoleSeen: true,
    cardiacActivity: "present",
    fetalHeartRateBpm: 160,
    numberOfGestations: 1,
    crownRumpLengthMm: null,
    meanSacDiameterMm: null,
    biparietalDiameterMm: null,
    headCircumferenceMm: null,
    abdominalCircumferenceMm: null,
    femurLengthMm: null,
    estimatedFetalWeightG: null,
    lmpDate: null,
    estimatedGestationalAgeWeeks: null,
    estimatedDueDate: null,
    fetalPresentation: "cephalic",
    amnioticFluidIndexCm: null,
    uterineFibroidsSeen: false,
    pelvicFreeFluid: "none",
  },
  notes: null,
};

const NORMAL_AORTA_WORKSHEET: AortaWorksheetData = {
  examType: "aorta",
  indication: null,
  views: {
    proximalTrans: true,
    proximalSag: true,
    midTrans: true,
    midSag: true,
    distalTrans: true,
    distalSag: true,
  },
  findings: {
    proximal: { ...NORMAL_AORTA_SEGMENT },
    mid: { ...NORMAL_AORTA_SEGMENT },
    distal: { ...NORMAL_AORTA_SEGMENT },
  },
  notes: null,
};

export const EMPTY_MSK_REGION: MskRegionData = {
  side: null,
  indication: null,
  findings: {},
};

const EMPTY_MSK_WORKSHEET: MskWorksheetData = {
  examType: "msk",
  indication: null,
  views: {},
  regions: Object.fromEntries(
    MSK_REGIONS.map((region) => [
      region,
      { ...EMPTY_MSK_REGION, findings: {} },
    ]),
  ) as Record<MskRegion, MskRegionData>,
  notes: null,
};

export const isProcedureExamType = (
  examType: string,
): examType is ProcedureExamType =>
  (PROCEDURE_EXAM_TYPES as readonly string[]).includes(examType);

export const isProcedureWorksheet = (
  data: ExamWorksheetData,
): data is ProcedureWorksheetData => isProcedureExamType(data.examType);

export const emptyProcedureWorksheet = (
  examType: ProcedureExamType,
): ProcedureWorksheetData => ({
  examType,
  definitionVersion: 1,
  indication: [],
  views: {},
  findings: {},
  notes: null,
});

export const isUsExamType = (examType: string): examType is UsExamType =>
  (US_EXAM_TYPES as readonly string[]).includes(examType);

export const isUsWorksheet = (
  data: ExamWorksheetData,
): data is UsWorksheetData => isUsExamType(data.examType);

export const usesMultiIndication = (examType: string): boolean =>
  isProcedureExamType(examType) || isUsExamType(examType);

export const isMultiIndicationWorksheet = (
  data: ExamWorksheetData,
): data is ProcedureWorksheetData | UsWorksheetData =>
  usesMultiIndication(data.examType);

export const emptyUsWorksheet = (examType: UsExamType): UsWorksheetData => ({
  examType,
  definitionVersion: 1,
  indication: [],
  views: {},
  findings: {},
  technicallyLimited: {},
  notes: null,
});

const EMPTY_US_WORKSHEETS = Object.fromEntries(
  US_EXAM_TYPES.map((examType) => [examType, emptyUsWorksheet(examType)]),
) as Record<UsExamType, UsWorksheetData>;

export const NORMAL_WORKSHEET_BY_TYPE: Record<ExamType, ExamWorksheetData> = {
  efast: NORMAL_EFAST_WORKSHEET,
  cardiac: NORMAL_CARDIAC_WORKSHEET,
  lung: NORMAL_LUNG_WORKSHEET,
  bladder: NORMAL_BLADDER_WORKSHEET,
  renal: NORMAL_RENAL_WORKSHEET,
  aorta: NORMAL_AORTA_WORKSHEET,
  pregnancy: NORMAL_PREGNANCY_WORKSHEET,
  msk: EMPTY_MSK_WORKSHEET,
  guided_joint_injection: emptyProcedureWorksheet("guided_joint_injection"),
  guided_tendon_injection: emptyProcedureWorksheet("guided_tendon_injection"),
  guided_nerve_block: emptyProcedureWorksheet("guided_nerve_block"),
  ...EMPTY_US_WORKSHEETS,
};

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// Returns a deep-cloned normal worksheet with the current indication preserved.
// Uses a discriminated-union switch so TypeScript can verify the indication is
// valid for the specific exam type — no casts needed.
export function cloneNormalWithIndication(
  current: ExamWorksheetData,
): ExamWorksheetData {
  if (isUsWorksheet(current)) {
    return {
      ...emptyUsWorksheet(current.examType),
      indication: [...current.indication],
    };
  }
  switch (current.examType) {
    case "efast":
      return {
        ...deepClone(NORMAL_EFAST_WORKSHEET),
        indication: current.indication,
      };
    case "cardiac":
      return {
        ...deepClone(NORMAL_CARDIAC_WORKSHEET),
        indication: current.indication,
      };
    case "lung":
      return {
        ...deepClone(NORMAL_LUNG_WORKSHEET),
        indication: current.indication,
      };
    case "bladder":
      return {
        ...deepClone(NORMAL_BLADDER_WORKSHEET),
        indication: current.indication,
      };
    case "renal":
      return {
        ...deepClone(NORMAL_RENAL_WORKSHEET),
        indication: current.indication,
      };
    case "aorta":
      return {
        ...deepClone(NORMAL_AORTA_WORKSHEET),
        indication: current.indication,
      };
    case "pregnancy":
      return {
        ...deepClone(NORMAL_PREGNANCY_WORKSHEET),
        indication: current.indication,
      };
    case "msk":
      return {
        ...deepClone(EMPTY_MSK_WORKSHEET),
        indication: current.indication,
      };
    case "guided_joint_injection":
    case "guided_tendon_injection":
    case "guided_nerve_block":
      return {
        ...emptyProcedureWorksheet(current.examType),
        indication: [...current.indication],
      };
    default: {
      const _exhaustive: never = current;
      throw new Error(
        `Unexpected exam type: ${(_exhaustive as ExamWorksheetData).examType}`,
      );
    }
  }
}

// ─── Empty defaults per exam type (used by All Normal toggle when cleared) ────
// Every view unselected and every finding un-assessed. FindingValue has no null
// member, so its "un-assessed" state is the explicit "not_assessed" literal;
// every other finding is nullable and defaults to null.

const UNASSESSED_FINDING: FindingValue = "not_assessed";

const EMPTY_EFAST_WORKSHEET: EFastWorksheetData = {
  examType: "efast",
  indication: null,
  views: {
    ruq: false,
    luq: false,
    suprapubic: false,
    subxiphoid: false,
    rLung: false,
    lLung: false,
  },
  findings: {
    ruqFreeFluid: UNASSESSED_FINDING,
    luqFreeFluid: UNASSESSED_FINDING,
    pelvicFreeFluid: UNASSESSED_FINDING,
    pericardialEffusion: UNASSESSED_FINDING,
    rPneumothorax: UNASSESSED_FINDING,
    lPneumothorax: UNASSESSED_FINDING,
  },
  notes: null,
};

const EMPTY_CARDIAC_WORKSHEET: CardiacWorksheetData = {
  examType: "cardiac",
  indication: null,
  views: { plax: false, psax: false, apical4Chamber: false, subxiphoid: false },
  findings: {
    lvFunction: null,
    rvDilatation: null,
    pericardialEffusion: null,
    ivcCollapsible: null,
    ivcDiameterCm: null,
    spectralDoppler: null,
    colorFlowDoppler: null,
  },
  notes: null,
};

const EMPTY_LUNG_ZONE: LungZoneFindings = {
  lungSliding: null,
  bLines: null,
  pleuralEffusion: null,
  consolidation: null,
};

const EMPTY_LUNG_WORKSHEET: LungWorksheetData = {
  examType: "lung",
  indication: null,
  views: {
    rUpperAnterior: false,
    rLowerAnterior: false,
    rPosterior: false,
    lUpperAnterior: false,
    lLowerAnterior: false,
    lPosterior: false,
  },
  zones: {
    rUpper: { ...EMPTY_LUNG_ZONE },
    rLower: { ...EMPTY_LUNG_ZONE },
    lUpper: { ...EMPTY_LUNG_ZONE },
    lLower: { ...EMPTY_LUNG_ZONE },
  },
  notes: null,
};

const EMPTY_BLADDER_WORKSHEET: BladderWorksheetData = {
  examType: "bladder",
  indication: null,
  views: { sagittal: false, transverse: false },
  findings: {
    bladderVolumeMethod: null,
    bladderVolumeMl: null,
    postVoidVolumeMl: null,
    distension: null,
    wallAbnormalities: null,
  },
  notes: null,
};

const EMPTY_KIDNEY: KidneyFindings = {
  sizeLengthCm: null,
  hydronephrosis: null,
  stones: null,
  perinephricFluid: null,
  cystOrMass: null,
};

const EMPTY_RENAL_WORKSHEET: RenalWorksheetData = {
  examType: "renal",
  indication: null,
  views: {
    rKidneyLong: false,
    rKidneyTrans: false,
    lKidneyLong: false,
    lKidneyTrans: false,
    bladder: false,
  },
  findings: {
    rKidney: { ...EMPTY_KIDNEY },
    lKidney: { ...EMPTY_KIDNEY },
    bladderAssessed: null,
  },
  notes: null,
};

const EMPTY_AORTA_SEGMENT: AortaSegmentFindings = {
  diameterCm: null,
  aneurysm: null,
  dissectionFlap: null,
  muralThrombus: null,
};

const EMPTY_AORTA_WORKSHEET: AortaWorksheetData = {
  examType: "aorta",
  indication: null,
  views: {
    proximalTrans: false,
    proximalSag: false,
    midTrans: false,
    midSag: false,
    distalTrans: false,
    distalSag: false,
  },
  findings: {
    proximal: { ...EMPTY_AORTA_SEGMENT },
    mid: { ...EMPTY_AORTA_SEGMENT },
    distal: { ...EMPTY_AORTA_SEGMENT },
  },
  notes: null,
};

const EMPTY_PREGNANCY_WORKSHEET: PregnancyWorksheetData = {
  examType: "pregnancy",
  indication: null,
  views: {
    transabdominalSagittal: false,
    transabdominalTransverse: false,
    transvaginalSagittal: false,
    transvaginalTransverse: false,
  },
  findings: {
    intrauterineGestation: null,
    gestationalSacSeen: null,
    yolkSacSeen: null,
    fetalPoleSeen: null,
    cardiacActivity: null,
    fetalHeartRateBpm: null,
    numberOfGestations: null,
    crownRumpLengthMm: null,
    meanSacDiameterMm: null,
    biparietalDiameterMm: null,
    headCircumferenceMm: null,
    abdominalCircumferenceMm: null,
    femurLengthMm: null,
    estimatedFetalWeightG: null,
    lmpDate: null,
    estimatedGestationalAgeWeeks: null,
    estimatedDueDate: null,
    fetalPresentation: null,
    amnioticFluidIndexCm: null,
    uterineFibroidsSeen: null,
    pelvicFreeFluid: null,
  },
  notes: null,
};

export const EXAM_TYPES = Object.keys(NORMAL_WORKSHEET_BY_TYPE) as ExamType[];

export const toGqlExamType = (examType: ExamType): EpocusExamType =>
  examType.toUpperCase() as EpocusExamType;

export const toExamTypes = (
  gqlExamTypes: readonly EpocusExamType[],
): ExamType[] =>
  gqlExamTypes
    .map((gqlExamType) => gqlExamType.toLowerCase())
    .filter(
      (examType): examType is ExamType => examType in NORMAL_WORKSHEET_BY_TYPE,
    );

export const EMPTY_WORKSHEET_BY_TYPE: Record<ExamType, ExamWorksheetData> = {
  efast: EMPTY_EFAST_WORKSHEET,
  cardiac: EMPTY_CARDIAC_WORKSHEET,
  lung: EMPTY_LUNG_WORKSHEET,
  bladder: EMPTY_BLADDER_WORKSHEET,
  renal: EMPTY_RENAL_WORKSHEET,
  aorta: EMPTY_AORTA_WORKSHEET,
  pregnancy: EMPTY_PREGNANCY_WORKSHEET,
  msk: EMPTY_MSK_WORKSHEET,
  guided_joint_injection: emptyProcedureWorksheet("guided_joint_injection"),
  guided_tendon_injection: emptyProcedureWorksheet("guided_tendon_injection"),
  guided_nerve_block: emptyProcedureWorksheet("guided_nerve_block"),
  ...EMPTY_US_WORKSHEETS,
};

// Mirror of cloneNormalWithIndication: a fully-unselected worksheet with the
// clinical indication and free-text notes carried over from the current data.
// Notes and indication are annotations, not view/finding state, so clearing
// "All Normal" must not discard them.
export function cloneEmptyWithIndication(
  current: ExamWorksheetData,
): ExamWorksheetData {
  if (isUsWorksheet(current)) {
    return {
      ...emptyUsWorksheet(current.examType),
      indication: [...current.indication],
      notes: current.notes,
    };
  }
  switch (current.examType) {
    case "efast":
      return {
        ...deepClone(EMPTY_EFAST_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "cardiac":
      return {
        ...deepClone(EMPTY_CARDIAC_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "lung":
      return {
        ...deepClone(EMPTY_LUNG_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "bladder":
      return {
        ...deepClone(EMPTY_BLADDER_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "renal":
      return {
        ...deepClone(EMPTY_RENAL_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "aorta":
      return {
        ...deepClone(EMPTY_AORTA_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "pregnancy":
      return {
        ...deepClone(EMPTY_PREGNANCY_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "msk":
      return {
        ...deepClone(EMPTY_MSK_WORKSHEET),
        indication: current.indication,
        notes: current.notes,
      };
    case "guided_joint_injection":
    case "guided_tendon_injection":
    case "guided_nerve_block":
      return {
        ...emptyProcedureWorksheet(current.examType),
        indication: [...current.indication],
        notes: current.notes,
      };
    default: {
      const _exhaustive: never = current;
      throw new Error(
        `Unexpected exam type: ${(_exhaustive as ExamWorksheetData).examType}`,
      );
    }
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  );
}

// True when every view is obtained and every finding sits at its normal
// default. Compares against the static normal template for this exam type,
// ignoring indication and notes (neither makes a worksheet abnormal). The
// All Normal toggle derives its state from this on every render, so this stays
// clone-free — the shallow spreads only override the two ignored keys.
export function isWorksheetAllNormal(data: ExamWorksheetData): boolean {
  if (isUsWorksheet(data)) return false;
  const normal = NORMAL_WORKSHEET_BY_TYPE[data.examType];
  return deepEqual(
    { ...data, indication: null, notes: null },
    { ...normal, indication: null, notes: null },
  );
}
